import type { GridSortModel, GridFilterItem } from "@mui/x-data-grid-pro"
import type { FormSchemaLeadsType } from "@/src/sections/blankForm/blankFormLeads"

import useSWR from "swr"
import dayjs from "dayjs"
import { useMemo } from "react"

import axiosInstance, { fetcher, endpoints } from "src/utils/axios"

import type { ApiResponse } from "../types/apiResponse"
import type { LabelValuePair } from "../types/labelValuePair"
import type { SpecialTags } from "../types/leadsEvaluationModel"
import type { LeadsModel, LeadTableFilters } from "../types/leadsModel"

// ----------------------------------------------------------------------

export const swrOptions = {
    revalidateOnFocus: false,
}

// ----------------------------------------------------------------------

type LeadsData = {
    data: { content: LeadsModel[]; totalElements: number | undefined; leadCountPerStatus: { [key: string]: number } }
}

export const tableFiltersToJson = (
    search: string,
    filterOptions: GridFilterItem[],
    extraFilters: { [key: string]: dayjs.Dayjs | LabelValuePair<string>[] | string[] | undefined }
) => {
    const allFilters: any = {}

    if (search) allFilters.search = search

    filterOptions.forEach((filter) => {
        if (filter.value) allFilters[filter.field] = Array.isArray(filter.value) ? filter.value.join(",") : filter.value
    })

    Object.entries(extraFilters).forEach((item) => {
        let value
        if (item[0] === "createdDate" && Array.isArray(item[1])) {
            const [createdDateStart, createdDateEnd] = item[1]
            delete allFilters.createdDate
            if (createdDateStart)
                allFilters.createdDateStart = dayjs(createdDateStart as string)
                    .startOf("day")
                    .toISOString()
            if (createdDateEnd)
                allFilters.createdDateEnd = dayjs(createdDateEnd as string)
                    .endOf("day")
                    .toISOString()
        } else if (item[0] === "callbackDate" && Array.isArray(item[1])) {
            const [callbackDateStart, callbackDateEnd] = item[1]
            delete allFilters.callbackDate
            if (callbackDateStart)
                allFilters.callbackDateStart = dayjs(callbackDateStart as string)
                    .startOf("day")
                    .toISOString()
            if (callbackDateEnd)
                allFilters.callbackDateEnd = dayjs(callbackDateEnd as string)
                    .endOf("day")
                    .toISOString()
        } else if (Array.isArray(item[1])) {
            if (item[0] === "aiMatchStatus") {
                value = item[1]
                    .map((e) => {
                        let currentElement = (e as any).value ?? e
                        if (currentElement === "NOT_COMPLETED") currentElement = "INCONCLUSIVE_CALL"
                        return currentElement
                    })
                    .join(",")
            } else value = item[1].map((e) => (e as any).value ?? e).join(",")
        } else {
            value = item[1]
        }
        if (dayjs.isDayjs(value)) value = dayjs(value).toISOString()
        if (value) allFilters[item[0]] = value
    })

    return allFilters
}

const convertDateFilterToIso = (value: dayjs.Dayjs | null, isStart: boolean) => {
    if (!value) return null
    if (isStart) {
        value = value.set("hour", 0).set("minute", 0).set("second", 0).set("millisecond", 0)
    } else {
        value = value.set("hour", 23).set("minute", 59).set("second", 59).set("millisecond", 999)
    }
    return value.toISOString()
}

export const getTableFilterString = (
    sortModel: GridSortModel,
    search: string,
    filterOptions: GridFilterItem[],
    extraFilters: {
        [key: string]: [dayjs.Dayjs | null, dayjs.Dayjs | null] | LabelValuePair<string>[] | string[] | undefined
    }
) => {
    const sortQuery = sortModel
        .map((e) => {
            const field = e.field === "matchScoreResult" ? "matchScore" : e.field
            return `${field},${e.sort}`
        })
        .join("&")
    let filters = filterOptions
        .filter((e) => e.value)
        .map((e) => `${e.field}=${e.value}`)
        .join("&")

    const extraFilterValues: string[] = []

    Object.entries(extraFilters).forEach((item) => {
        let value
        if (Array.isArray(item[1])) {
            // Date filters are sent in the form of `createdDateStart=2025-04-30T18:30:00.000Z&createdDateEnd=2025-04-30T18:30:00.000Z`
            if (dayjs.isDayjs(item[1][0]) || dayjs.isDayjs(item[1][1])) {
                const startDate = dayjs.isDayjs(item[1][0]) ? dayjs(item[1][0]) : null
                const endDate = dayjs.isDayjs(item[1][1]) ? dayjs(item[1][1]) : null
                if (startDate) {
                    extraFilterValues.push(`${item[0]}Start=${convertDateFilterToIso(startDate, true)}`)
                }
                if (endDate) {
                    extraFilterValues.push(`${item[0]}End=${convertDateFilterToIso(endDate, false)}`)
                }
            } else {
                value = item[1]
                    .map((e) => {
                        const v = (e as any).value ?? e
                        if (item[0] === "aiMatchStatus" && v === "NOT_COMPLETED") {
                            // Needed only for get all leads API
                            return "INCONCLUSIVE_CALL"
                        }
                        return v
                    })
                    .join(",")

                if (value) extraFilterValues.push(`${item[0]}=${value}`)
            }
        } else {
            value = item[1]
            if (value) extraFilterValues.push(`${item[0]}=${value}`)
        }
    })
    if (extraFilterValues.length) {
        filters = filters ? `${filters}&${extraFilterValues.join("&")}` : extraFilterValues.join("&")
    }

    return `${sortQuery ? `&sort=${sortQuery}` : ""}${search ? `&search=${search}` : ""}${filters ? `&${filters}` : ""}`
}

export function useGetLeads(
    page: number,
    size: number,
    sortModel: GridSortModel,
    search: string,
    filterOptions: GridFilterItem[],
    extraFilters: LeadTableFilters
) {
    const url = endpoints.leads.list
    const filterString = getTableFilterString(sortModel, search, filterOptions, extraFilters)

    const { data, isLoading, error, isValidating, mutate } = useSWR<LeadsData>(
        `${url}?page=${page}&size=${size}${filterString}`,
        fetcher,
        swrOptions
    )

    const memoizedValue = useMemo(() => {
        const l = (data?.data?.content || []).map(leadsMapper)
        //l = [...l, ...l, ...l, ...l].map((e, i) => ({ ...e, id: `${e?.id}${i}` }))
        return {
            leads: l,
            leadsCount: data?.data?.totalElements,
            leadsLoading: isLoading,
            tabData: data?.data?.leadCountPerStatus,
            leadsError: error,
            leadsValidating: isValidating,
            leadsEmpty: !isLoading && !data?.data?.content.length,
            leadsMutate: mutate,
        }
    }, [data, error, isLoading, isValidating, mutate])

    return memoizedValue
}

// ----------------------------------------------------------------------

type LeadData = {
    data: LeadsModel
}

export function useGetLead({ leadId, page = 0, size }: { leadId: string | undefined; page: number; size: number }) {
    const url = leadId
        ? [`${endpoints.leads.details}${leadId}`, { params: { page, size, sort: "createdDate%2Cdesc" } }]
        : ""

    const { data, isLoading, error, isValidating, mutate } = useSWR<LeadData>(url, fetcher, swrOptions)

    const memoizedValue = useMemo(
        () => ({
            lead: leadsMapper(data?.data),
            leadLoading: isLoading,
            leadError: error,
            leadValidating: isValidating,
            leadMutate: mutate,
        }),
        [data, error, isLoading, isValidating, mutate]
    )

    return memoizedValue
}

const mapSpecialTags = (specialTags: SpecialTags | undefined) => {
    if (!specialTags) return undefined

    return {
        positiveMarkers: specialTags.positiveMarkers?.filter((marker) => !!marker.tag?.length),
        averageMarkers: specialTags.averageMarkers?.filter((marker) => !!marker.tag?.length),
        missingMarkers: specialTags.missingMarkers?.filter((marker) => !!marker.tag?.length),
        negativeMarkers: specialTags.negativeMarkers?.filter((marker) => !!marker.tag?.length),
        redFlagMarkers: specialTags.redFlagMarkers.filter((marker) => !!marker.tag?.length),
    }
}

const leadsMapper = (data: any | undefined): LeadsModel | undefined => {
    if (!data) return undefined

    return {
        ...data,
        assignedTo: { label: data.assignedTo?.jobTitle, value: data.assignedTo?.id },
        createdBy: { label: data.createdBy?.name, value: data.createdBy?.id },
        mandate: { label: data.mandate?.displayName, value: data.mandate?.id },
        client: { label: data.client?.name, value: data.client?.id },
        mandateJobTitle: data.mandate?.jobTitle,
        mandateCity: data.mandate?.city,
        aiEvaluationSummary: data.aiEvaluationSummary
            ? {
                  ...data.aiEvaluationSummary,
                  furtherFitmentBreakdownEvaluation: {
                      ...data.aiEvaluationSummary?.furtherFitmentBreakdownEvaluation,
                      specialTags: mapSpecialTags(
                          data.aiEvaluationSummary?.furtherFitmentBreakdownEvaluation?.specialTags
                      ),
                  },
              }
            : undefined,
    }
}

export const updateLeadDetails = (
    data: Partial<LeadsModel> & {
        id: string
    }
) => {
    return axiosInstance.patch(endpoints.leads.statusUpdate, {
        leadId: data.id,
        status: data.status,
        subStatus: data.subStatus,
        comment: data.comments,
        resumeLink: data.resumeLink,
        callbackDate: data.callbackDate ? dayjs(data.callbackDate).format() : undefined,
        assignedTo: data.assignedTo?.value,
        reason: data.reason,
    })
}

export const updateLeadsForm = (payload: FormSchemaLeadsType) => {
    return axiosInstance.post(endpoints.leads.leadsForm, payload)
}

export const updateLeadsFormPublic = (payload: FormSchemaLeadsType, token: string) => {
    return axiosInstance.post(endpoints.leads.creatLeads, payload, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    })
}

export const updateLeadsFormPublicV2 = (payload: FormSchemaLeadsType, token: string) => {
    return axiosInstance.post(endpoints.leads.publicCreateLeadsV2, payload, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    })
}

export const editLeadsForm = (payload: FormSchemaLeadsType, id: string) => {
    return axiosInstance.put(`${endpoints.leads.update}/${id}`, payload)
}

export const addInterviewLeadsFormWithoutScreening = async (payload: FormSchemaLeadsType) => {
    const request = await axiosInstance.post(endpoints.leads.leadsForm, {
        ...payload,
        status: "NEW_LEAD",
        subStatus: null,
    })
    const { data } = request.data
    const interviewRequest = await axiosInstance.post(endpoints.interviews.post, {
        leadId: data.id,
        interviewMode: "IN_PERSON",
        status: payload.status,
        interviewDate: payload.interviewDate,
        callbackDate: payload.callbackDate,
        subStatus: payload.subStatus,
        reason: payload.comments,
    })
    return { isBalanceInsufficientForAi: data.aiMatchStatus === "NOT_SCREENED_INSUFFICIENT_CREDITS", interviewRequest }
}

export const addSelectionLeadsFormWithoutScreening = async (id: string, payload: FormSchemaLeadsType) => {
    return axiosInstance.post(endpoints.selections.selectionForm, {
        leadId: id,
        comments: "",
        callbackDate: payload.callbackDate,
        selectionDate: payload.selectionDate,
        joiningDate: payload.joiningDate,
        expectedJoiningDate: payload.expectedJoiningDate,
        subStatus: payload.subStatus,
        status: payload.status,
        joiningConfirmationProofLink: "",
        selectionConfirmationProofLink: window.location.href,
    })
}

export const requestPublicInterviewPhoneCall = async (
    participantId: string,
    token: string
): Promise<ApiResponse<string>> => {
    return axiosInstance
        .post(endpoints.leads.requestPublicInterviewPhoneCall(participantId), null, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        .then((resp): ApiResponse<string> => {
            return {
                status: "success",
                data: resp?.data?.data,
            }
        })
        .catch((e): ApiResponse<string> => {
            console.log(e)
            return {
                status: "error",
                errorMessage: e.status,
            }
        })
}

export const generateOtpForCandidate = async (
    phoneNumber: string | undefined | null,
    email: string | undefined | null
): Promise<ApiResponse<string>> => {
    return axiosInstance
        .post(endpoints.leads.generateOtpForCandidate, {
            phoneNumber: phoneNumber || undefined,
            email,
        })
        .then((resp): ApiResponse<string> => {
            return {
                status: "success",
                data: resp?.data?.message,
            }
        })
        .catch((e): ApiResponse<string> => {
            console.log(e)
            return {
                status: "error",
                errorMessage: e.status,
            }
        })
}
